// Which fields of an edit screen hold an unsaved change (TGCLOUD-1902).
//
// The one decision every edit screen shares: a field is changed when its value
// differs from the BASELINE, the record as last saved, by compareFieldValues.
// Gold, the per-section markers and the unsaved count all read this, so they
// cannot disagree.
//
// THE BASELINE is the form's own seeded values, captured by the caller once its
// mount-time work is done (a reshaped record, an age worked out from the date of
// birth, a defaulted "recorded by"). Never the raw server record: that would
// show changes nobody made. It moves only when:
//
//   - `resetKey` changes (another record, another person; or the record has
//     finished loading): the baseline is re-read from `baseline`, in the same
//     render, so nothing flashes gold in between;
//   - the caller calls `resetBaseline(next)` after a CONFIRMED save (rule 7),
//     with what the server accepted. A failed save never calls it, so the gold
//     stays.
//
// `mode: 'create'` reports nothing whatever the values: on a new record every
// field would be gold, which tells nobody anything (rule 6). What a new record
// holds is still unsaved, though, so `unsavedCount` counts the fields that
// differ from the baseline in EITHER mode: the exit guards ask by it, the gold
// and the count by `count`.
//
// `fields` names what is compared, in screen order: the count is the number of
// fields drawn gold, so a key of form state that is not a field on screen must
// not be in it. An entry is a key of the form's values, or a descriptor for a
// field that is not one key:
//
//   { name, read, readSaved, compare }
//     read(values)              the field's value now, and (by default) as saved:
//                               a date held as three keys, read as one
//     readSaved(saved, values)  the saved value when it depends on the form now:
//                               the union a partner select is showing
//     compare                   compareFieldValues options for this field
//
// `exclude` (names, a Set, or a predicate) takes out the fields the system
// recalculates (rule 8), including one that is disabled right now. `compare`
// gives compareFieldValues options per field, as an object or a function of the
// name.

import { useCallback, useMemo, useState } from 'react';
import { compareFieldValues } from './compareFieldValues';

const NO_VALUES = Object.freeze({});
const NO_NAMES = Object.freeze([]);
const SEPARATOR = '\u0000';

const isExcluded = (exclude, name) => {
  if (!exclude) return false;
  if (typeof exclude === 'function') return Boolean(exclude(name));
  if (exclude instanceof Set) return exclude.has(name);
  if (Array.isArray(exclude)) return exclude.includes(name);
  return false;
};

const optionsFor = (compare, name) =>
  (typeof compare === 'function' ? compare(name) : compare?.[name]) ||
  undefined;

const nameOf = (field) =>
  field && typeof field === 'object' ? field.name : field;

const fieldList = (fields, baseline, values) => {
  if (Array.isArray(fields)) return fields;
  return [...new Set([...Object.keys(baseline), ...Object.keys(values)])];
};

const readField = (field, baseline, values) => {
  if (!field || typeof field !== 'object') {
    return { was: baseline[field], now: values[field] };
  }
  const now = field.read ? field.read(values) : values[field.name];
  const was = field.readSaved
    ? field.readSaved(baseline, values)
    : field.read
    ? field.read(baseline)
    : baseline[field.name];
  return { was, now };
};

/**
 * The changed fields of a form, without React: what useChangedFields reports.
 *
 * @returns {{ changed: Object<string, {was: *, now: *}>, names: string[] }}
 *   `names` in field order
 */
export function changedFieldsOf({
  baseline,
  values,
  mode = 'edit',
  fields,
  exclude,
  compare
} = {}) {
  const changed = {};
  const names = [];
  if (mode === 'create') return { changed, names };
  const base = baseline || NO_VALUES;
  const now = values || NO_VALUES;
  for (const field of fieldList(fields, base, now)) {
    const name = nameOf(field);
    if (isExcluded(exclude, name)) continue;
    const { was, now: value } = readField(field, base, now);
    const options =
      (field && typeof field === 'object' && field.compare) ||
      optionsFor(compare, name);
    if (compareFieldValues(was, value, options)) continue;
    changed[name] = { was, now: value };
    names.push(name);
  }
  return { changed, names };
}

/**
 * @param {object} args
 * @param {object} args.baseline  the form's values as last saved (seeded)
 * @param {object} args.values    the form's values now
 * @param {'edit'|'create'} [args.mode]
 * @param {Array<string|object>} [args.fields]  the fields on screen, in screen order
 * @param {string[]|Set<string>|Function} [args.exclude]  never gold
 * @param {object|Function} [args.compare]  compareFieldValues options per field
 * @param {*} [args.resetKey]  re-read the baseline when this changes
 * @returns {{
 *   changed: Object<string, {was: *, now: *}>,
 *   changedNames: string[],
 *   count: number,
 *   isChanged: boolean,
 *   unsavedCount: number,
 *   isFieldChanged: (name: string) => boolean,
 *   nextChangedField: (fromField?: string|null) => string|null,
 *   resetBaseline: (next: object) => void,
 *   baseline: object
 * }}
 */
export default function useChangedFields({
  baseline,
  values,
  mode = 'edit',
  fields,
  exclude,
  compare,
  resetKey
} = {}) {
  const [held, setHeld] = useState(() => ({
    key: resetKey,
    base: baseline || NO_VALUES
  }));

  // A new record (or one that has just loaded): read the baseline again, now,
  // so that this render already compares against it (React's pattern for
  // state that follows a prop; it re-renders before anything is committed).
  let base = held.base;
  if (!Object.is(held.key, resetKey)) {
    base = baseline || NO_VALUES;
    setHeld({ key: resetKey, base });
  }

  const resetBaseline = useCallback(
    (next) => setHeld((prev) => ({ key: prev.key, base: next || NO_VALUES })),
    []
  );

  const all = changedFieldsOf({
    baseline: base,
    values,
    mode: 'edit',
    fields,
    exclude,
    compare
  });
  const creating = mode === 'create';
  const changed = creating ? {} : all.changed;
  const names = creating ? [] : all.names;

  // The same array for as long as the same fields are changed, so an effect
  // that reports them runs when the set changes, not on every keystroke.
  const signature = names.join(SEPARATOR);
  const changedNames = useMemo(
    () => (signature ? signature.split(SEPARATOR) : NO_NAMES),
    [signature]
  );

  // Every field in screen order, so "next" from an unchanged field is the next
  // changed one below it.
  const order = Array.isArray(fields) ? fields.map(nameOf) : null;
  const orderSignature = order ? order.join(SEPARATOR) : '';

  const isFieldChanged = useCallback(
    (name) => changedNames.includes(name),
    [changedNames]
  );

  const nextChangedField = useCallback(
    (fromField = null) => {
      if (changedNames.length === 0) return null;
      if (fromField === null || fromField === undefined) return changedNames[0];
      const at = changedNames.indexOf(fromField);
      if (at !== -1) return changedNames[(at + 1) % changedNames.length];
      const all = orderSignature ? orderSignature.split(SEPARATOR) : [];
      const from = all.indexOf(fromField);
      if (from !== -1) {
        const after = changedNames.find((name) => all.indexOf(name) > from);
        if (after) return after;
      }
      return changedNames[0];
    },
    [changedNames, orderSignature]
  );

  return {
    changed,
    changedNames,
    count: changedNames.length,
    isChanged: changedNames.length > 0,
    unsavedCount: all.names.length,
    isFieldChanged,
    nextChangedField,
    resetBaseline,
    baseline: base
  };
}
